import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import {
    Box,
    Typography,
    TextField,
    Button,
    Chip,
    Switch,
    FormControlLabel,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    CircularProgress,
} from '@mui/material';
import { Save as SaveIcon, Link as LinkIcon } from '@mui/icons-material';
import { AdminPageContainer, StaticCard } from '../../../components/admin/sharedStyles';
import PageHeader from '../../../components/PageHeader';
import ConfigSubMenu from '../../../components/admin/ConfigSubMenu';
import { useDispatch } from 'react-redux';
import { showAlert } from '../../../redux/slices/alerts';
import { getConfigValue, saveConfig } from '../../../redux/slices/config';
import { getInboundMailOauthStatus } from '../../../redux/ducks/inboundMail';
import { getApiUrl } from '../../../config';
import useUnsavedChanges from '../../../hooks/useUnsavedChanges';
import ConfirmDialog from '../../../components/admin/ConfirmDialog';
import { useBrandName } from '../../../utils/brandUtils';
import { formatDateTime } from '../../../utils/dateUtils';

const LoadingRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
}));

const SectionCard = styled(StaticCard)(({ theme }) => ({
    marginBottom: theme.spacing(3),
}));

const SectionHelper = styled(Typography)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const ToggleRow = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const ActionsRow = styled(Box)({
    display: 'flex',
    justifyContent: 'flex-end',
});

const ConnectionStatusRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(0.5),
}));

const ConnectionMetaCaption = styled(Typography)(({ theme }) => ({
    display: 'block',
    color: theme.palette.text.secondary,
}));

const DEFAULT_CONFIG = {
    enabled: false,
    host: '',
    port: 993,
    secure: true,
    user: '',
    authType: 'oauth2',
    password: '',
    oauth: {
        provider: 'google',
        tenantId: 'common',
    },
    inboundDomain: '',
    replyTo: '',
    createProjectsFromColdEmail: true,
    matchBySubjectDays: 30,
    maxProjectsPerSenderPerHour: 5,
    maxAttachmentBytes: 10485760,
    maxAttachmentCount: 10,
    maxMessagesPerPoll: 50,
    processedFolder: '',
};

const InboundMailConfig = () => {
    const dispatch = useDispatch();
    const brandName = useBrandName();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [config, setConfig] = useState(DEFAULT_CONFIG);
    const [oauthStatus, setOauthStatus] = useState(null);
    const [oauthStatusLoading, setOauthStatusLoading] = useState(true);

    const loadOauthStatus = () => {
        setOauthStatusLoading(true);
        dispatch(getInboundMailOauthStatus((data) => {
            setOauthStatus(data);
            setOauthStatusLoading(false);
        }));
    };

    useEffect(() => {
        loadConfig();
        loadOauthStatus();
        // Handle mailbox OAuth callback redirect query params on landing.
        const params = new URLSearchParams(window.location.search);
        if (params.get('inboundMailConnected') === 'true') {
            dispatch(showAlert({ severity: 'success', message: 'Mailbox authorized successfully' }));
            params.delete('inboundMailConnected');
            const next = params.toString();
            window.history.replaceState({}, '', `${window.location.pathname}${next ? `?${next}` : ''}`);
        } else if (params.get('inboundMailError')) {
            dispatch(showAlert({ severity: 'error', message: `Mailbox authorization failed: ${params.get('inboundMailError')}` }));
            params.delete('inboundMailError');
            const next = params.toString();
            window.history.replaceState({}, '', `${window.location.pathname}${next ? `?${next}` : ''}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadConfig = () => {
        setLoading(true);
        dispatch(getConfigValue({
            key: 'inboundMailConfig',
            value: null,
            callback: (data) => {
                if (data?.value) {
                    // Merge so fields added after the doc was first saved still get defaults.
                    // Secrets/tokens are not stored here: client id/secret + signing secret live in the
                    // server environment, the refresh token in the server-only inboundMailOauth key.
                    const { refreshToken, authorizedEmail, connectedAt, clientId, clientSecret, ...savedOauth } = data.value.oauth || {};
                    const { signingSecret, ...savedValue } = data.value;
                    setConfig({ ...DEFAULT_CONFIG, ...savedValue, oauth: { ...DEFAULT_CONFIG.oauth, ...savedOauth } });
                } else {
                    // No config doc yet (404): start from defaults; the first Save creates it.
                    setConfig(DEFAULT_CONFIG);
                }
                setLoading(false);
            }
        }));
    };

    const { showPrompt, confirmLeave, cancelLeave, message: unsavedMessage } = useUnsavedChanges(isDirty);

    const handleChange = (e) => {
        setIsDirty(true);
        const { name, value, checked, type } = e.target;
        setConfig((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : type === 'number' ? Number(value) : value,
        }));
    };

    const handleOauthChange = (e) => {
        setIsDirty(true);
        const { name, value } = e.target;
        setConfig((prev) => ({
            ...prev,
            oauth: { ...prev.oauth, [name]: value },
        }));
    };

    const handleAuthorizeMailbox = () => {
        window.location.href = `${getApiUrl()}/inbound-mail/oauth/start`;
    };

    const credentialsConfigured = Boolean(oauthStatus?.credentialsConfigured);
    const authorizeDisabled = config.authType !== 'oauth2' || !credentialsConfigured || isDirty;
    const authorizeHelper = !credentialsConfigured
        ? 'OAuth client credentials are not set in the server environment (INBOUND_MAIL_OAUTH_CLIENT_ID / INBOUND_MAIL_OAUTH_CLIENT_SECRET). Add them to the deployment, then authorize.'
        : isDirty
            ? 'Save your changes first — authorization uses the saved provider settings.'
            : `You'll be redirected to the provider to grant ${brandName} access to the mailbox, then returned here.`;

    const handleSave = () => {
        setSaving(true);
        dispatch(saveConfig({
            key: 'inboundMailConfig',
            value: config,
            callback: (data) => {
                setSaving(false);
                if (data?.status === 0) return;
                setIsDirty(false);
                dispatch(showAlert({ severity: 'success', message: 'Inbound mail settings saved successfully' }));
            }
        }));
    };

    return (
        <AdminPageContainer>
            <PageHeader
                title="Configuration"
                subtitle="Manage system configuration settings"
            />

            <ConfigSubMenu />

            {loading ? (
                <LoadingRow>
                    <CircularProgress />
                </LoadingRow>
            ) : (
                <>
                    <SectionHelper variant="body2" color="text.secondary">
                        Let clients reply to notification emails. {brandName} checks a mailbox on a schedule, posts replies as comments, and can turn brand-new emails into project requests.
                    </SectionHelper>
                    <SectionCard>
                        <Typography variant="h6" gutterBottom>
                            Mailbox
                        </Typography>
                        <SectionHelper variant="body2" color="text.secondary">
                            The IMAP mailbox {brandName} watches for incoming email. Nothing is read until the switch below is on.
                        </SectionHelper>
                        <ToggleRow>
                            <FormControlLabel
                                control={
                                    <Switch
                                        name="enabled"
                                        checked={config.enabled}
                                        onChange={handleChange}
                                    />
                                }
                                label="Enable inbound email processing"
                            />
                            <Typography variant="body2" color="text.secondary">
                                Master switch. When off, the mailbox is never contacted and no emails are processed.
                            </Typography>
                        </ToggleRow>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="IMAP Host"
                                    name="host"
                                    value={config.host}
                                    onChange={handleChange}
                                    placeholder="imap.gmail.com"
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    label="IMAP Port"
                                    name="port"
                                    type="number"
                                    value={config.port}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            name="secure"
                                            checked={config.secure}
                                            onChange={handleChange}
                                        />
                                    }
                                    label="Use TLS/SSL"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Mailbox Address"
                                    name="user"
                                    value={config.user}
                                    onChange={handleChange}
                                    placeholder="inbox@example.com"
                                    helperText={`The email account ${brandName} signs in to.`}
                                />
                            </Grid>
                        </Grid>
                    </SectionCard>

                    <SectionCard>
                        <Typography variant="h6" gutterBottom>
                            Authentication
                        </Typography>
                        <SectionHelper variant="body2" color="text.secondary">
                            How {brandName} signs in to the mailbox. Modern providers (Google Workspace, Microsoft 365) require OAuth2; older servers use a password. The OAuth app's Client ID and Secret are set in the server environment (INBOUND_MAIL_OAUTH_CLIENT_ID / INBOUND_MAIL_OAUTH_CLIENT_SECRET); choose the provider here, then click Authorize.
                        </SectionHelper>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel id="inbound-auth-type-label">Authentication Method</InputLabel>
                                    <Select
                                        labelId="inbound-auth-type-label"
                                        label="Authentication Method"
                                        name="authType"
                                        value={config.authType || 'oauth2'}
                                        onChange={handleChange}
                                    >
                                        <MenuItem value="oauth2">OAuth2 (Google / Microsoft)</MenuItem>
                                        <MenuItem value="password">Password (legacy servers)</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            {config.authType === 'password' && (
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Password"
                                        name="password"
                                        type="password"
                                        value={config.password}
                                        onChange={handleChange}
                                        helperText="App password or mailbox password."
                                    />
                                </Grid>
                            )}
                            {config.authType === 'oauth2' && (
                                <>
                                    <Grid item xs={12} md={6}>
                                        <FormControl fullWidth>
                                            <InputLabel id="inbound-oauth-provider-label">Provider</InputLabel>
                                            <Select
                                                labelId="inbound-oauth-provider-label"
                                                label="Provider"
                                                name="provider"
                                                value={config.oauth.provider || 'google'}
                                                onChange={handleOauthChange}
                                            >
                                                <MenuItem value="google">Google Workspace</MenuItem>
                                                <MenuItem value="microsoft">Microsoft 365</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    {config.oauth.provider === 'microsoft' && (
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="Tenant ID"
                                                name="tenantId"
                                                value={config.oauth.tenantId}
                                                onChange={handleOauthChange}
                                                helperText="Azure AD tenant. Leave as 'common' unless you use a specific tenant."
                                            />
                                        </Grid>
                                    )}
                                    <Grid item xs={12}>
                                        <ConnectionStatusRow>
                                            {oauthStatusLoading ? (
                                                <CircularProgress size={20} />
                                            ) : oauthStatus?.connected ? (
                                                <Chip
                                                    size="small"
                                                    color="success"
                                                    variant="outlined"
                                                    label={`Authorized as ${oauthStatus.authorizedEmail || 'mailbox owner'}`}
                                                />
                                            ) : (
                                                <Chip size="small" color="default" variant="outlined" label="Not authorized yet" />
                                            )}
                                            <Button
                                                variant={oauthStatus?.connected ? 'outlined' : 'contained'}
                                                color="primary"
                                                size="small"
                                                startIcon={<LinkIcon />}
                                                onClick={handleAuthorizeMailbox}
                                                disabled={authorizeDisabled}
                                            >
                                                {oauthStatus?.connected ? 'Re-authorize mailbox' : 'Authorize mailbox'}
                                            </Button>
                                        </ConnectionStatusRow>
                                        <ConnectionMetaCaption variant="caption">
                                            {authorizeHelper}
                                            {oauthStatus?.connected && oauthStatus.connectedAt
                                                ? ` Authorized on ${formatDateTime(oauthStatus.connectedAt)}.`
                                                : ''}
                                        </ConnectionMetaCaption>
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </SectionCard>

                    <SectionCard>
                        <Typography variant="h6" gutterBottom>
                            Reply Routing
                        </Typography>
                        <SectionHelper variant="body2" color="text.secondary">
                            Outgoing comment notifications carry a signed reply token so that when someone hits Reply, {brandName} knows exactly which item the email belongs to.
                        </SectionHelper>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Inbound Domain"
                                    name="inboundDomain"
                                    value={config.inboundDomain}
                                    onChange={handleChange}
                                    placeholder="example.com"
                                    helperText="Domain used in the Message-ID of outgoing notifications."
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Reply-To Address"
                                    name="replyTo"
                                    value={config.replyTo}
                                    onChange={handleChange}
                                    placeholder="inbox@example.com"
                                    helperText={`Where replies are sent — normally the same mailbox ${brandName} watches.`}
                                />
                            </Grid>
                        </Grid>
                        <SectionHelper variant="body2" color="text.secondary">
                            The reply-token signing secret is set in the server environment (INBOUND_MAIL_SIGNING_SECRET), not here.
                        </SectionHelper>
                    </SectionCard>

                    <SectionCard>
                        <Typography variant="h6" gutterBottom>
                            Behavior
                        </Typography>
                        <SectionHelper variant="body2" color="text.secondary">
                            What {brandName} does with the email it picks up, and the safety limits applied to each poll.
                        </SectionHelper>
                        <ToggleRow>
                            <FormControlLabel
                                control={
                                    <Switch
                                        name="createProjectsFromColdEmail"
                                        checked={config.createProjectsFromColdEmail}
                                        onChange={handleChange}
                                    />
                                }
                                label="Create projects from new emails"
                            />
                            <Typography variant="body2" color="text.secondary">
                                When a known client emails the mailbox directly (not a reply), turn it into a project request on their account. When off, those emails are parked for triage instead.
                            </Typography>
                        </ToggleRow>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Match Replies by Subject (days)"
                                    name="matchBySubjectDays"
                                    type="number"
                                    value={config.matchBySubjectDays}
                                    onChange={handleChange}
                                    helperText="Last resort when a reply carries no recognisable thread: attach it to a recent email with the same subject on the sender's account. 0 turns subject matching off."
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Max New Projects per Sender per Hour"
                                    name="maxProjectsPerSenderPerHour"
                                    type="number"
                                    value={config.maxProjectsPerSenderPerHour}
                                    onChange={handleChange}
                                    helperText="Stops a misbehaving mail loop from filling an account with projects. Further emails from that sender are parked for triage. 0 means no limit."
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Max Attachment Size (bytes)"
                                    name="maxAttachmentBytes"
                                    type="number"
                                    value={config.maxAttachmentBytes}
                                    onChange={handleChange}
                                    helperText="Attachments larger than this are skipped. 0 means no limit."
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Max Attachments per Email"
                                    name="maxAttachmentCount"
                                    type="number"
                                    value={config.maxAttachmentCount}
                                    onChange={handleChange}
                                    helperText="Extra attachments beyond this are skipped. 0 means no limit."
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Max Emails per Poll"
                                    name="maxMessagesPerPoll"
                                    type="number"
                                    value={config.maxMessagesPerPoll}
                                    onChange={handleChange}
                                    helperText="Emails beyond this wait for the next poll a couple of minutes later."
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Processed Folder"
                                    name="processedFolder"
                                    value={config.processedFolder}
                                    onChange={handleChange}
                                    helperText="Leave empty to mark messages as read instead of moving them."
                                />
                            </Grid>
                        </Grid>
                    </SectionCard>

                    <ActionsRow>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<SaveIcon />}
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </ActionsRow>
                </>
            )}

            <ConfirmDialog
                open={showPrompt}
                onClose={cancelLeave}
                onConfirm={confirmLeave}
                title="Unsaved Changes"
                message={unsavedMessage}
                confirmLabel="Leave"
                confirmColor="warning"
            />
        </AdminPageContainer>
    );
};

export default InboundMailConfig;
